import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {InputChangeEvent} from 'util/Types';

import ChangeStudyableAssignmentPermissionWarningBanner, {
  ChangeStudyableAssignmentPermissionOriginType,
} from './studyable/ChangeStudyableAssignmentPermissionWarningBanner';
import {useCanChangeStudyableAssignmentStudiesQuery} from './studyable/useCanChangeStudyableAssignmentQuery';
import {StudyAssociation} from './StudyableDAO';
import StudyAutoLinkingInput from './StudyAutoLinkingInput';
import StudySearchInput, {StudySearchControlledItemsProps, StudySearchItem} from './StudySearchInput';

export interface StudyAssociationInputProps {
  studySearchControlledItemsProps: StudySearchControlledItemsProps;
  invalidItemNames: Array<string>;
  isAutoLinkingChecked: boolean;
  isAutoLinkingDisabled: boolean;
  onAutoLinkingCheckedChange: (e: InputChangeEvent) => void;
  onAutoLinkingStudyChange: (studyId: string) => void;
  displayPermissionWarning?: boolean;
  // The study that the item will be linked to
  autoLinkingStudy?: {
    id: string;
    name: string;
  };
  // If false, the auto-linking study is locked to its initial value.
  // If true, the user can pick the auto-linking study from a dropdown.
  enableAutoLinkingSelection?: boolean;
}

export default function StudyAssociationInput({
  studySearchControlledItemsProps,
  invalidItemNames,
  isAutoLinkingChecked,
  isAutoLinkingDisabled,
  onAutoLinkingCheckedChange,
  onAutoLinkingStudyChange,
  displayPermissionWarning,
  autoLinkingStudy,
  enableAutoLinkingSelection = false,
}: StudyAssociationInputProps): JSX.Element {
  return (
    <>
      <StudySearchInput {...studySearchControlledItemsProps} shouldAllowMultiple={true} />
      {displayPermissionWarning && invalidItemNames.length > 0 && (
        <div className={css(styles.warningBanner)}>
          <ChangeStudyableAssignmentPermissionWarningBanner
            warningOriginType={ChangeStudyableAssignmentPermissionOriginType.STUDY}
            invalidStudyNames={invalidItemNames}
          />
        </div>
      )}
      <StudyAutoLinkingInput
        selectedStudies={studySearchControlledItemsProps.selectedItems}
        invalidItemNames={invalidItemNames}
        isAutoLinkingChecked={isAutoLinkingChecked}
        isAutoLinkingDisabled={isAutoLinkingDisabled}
        onAutoLinkingCheckedChange={onAutoLinkingCheckedChange}
        onAutoLinkingStudyChange={onAutoLinkingStudyChange}
        enableAutoLinkingSelection={enableAutoLinkingSelection}
        autoLinkingStudy={autoLinkingStudy}
        autoLinkingExtensionsFlagEnabled={BenchlingConfig.STUDIES_ENTRY_AUTO_LINKING_EXTENSIONS}
      />
    </>
  );
}

export function useStudyAssociationInputProps({
  initialStudySearchItems,
  displayPermissionWarning,
  initialAutoLinkingStudy,
}: {
  initialStudySearchItems: Array<StudySearchItem>;
  displayPermissionWarning?: boolean;
  initialAutoLinkingStudy?: {
    id: string;
    name: string;
  };
}): StudyAssociationInputProps {
  const hasInitialAutoLinkingStudy = initialAutoLinkingStudy !== undefined;
  const [studySearchItems, setStudySearchItems] = useState<Array<StudySearchItem>>(initialStudySearchItems);
  const [autoLinkingStudyId, setAutoLinkingStudyId] = useState<string | undefined>(
    initialAutoLinkingStudy?.id ?? studySearchItems[0]?.id
  );
  const studyIds = studySearchItems.map((item) => item.id);
  const autoLinkingStudy =
    initialAutoLinkingStudy ?? studySearchItems.find((study) => study.id === autoLinkingStudyId);
  const isAutoLinkingStudySelected = autoLinkingStudy !== undefined && studyIds.includes(autoLinkingStudy.id);
  const [isAutoLinkingChecked, setIsAutoLinkingChecked] = useState(
    hasInitialAutoLinkingStudy && isAutoLinkingStudySelected
  );
  // Get studies missing the change studyable assignment permission
  const studyNamesToStudyablePermission = useCanChangeStudyableAssignmentStudiesQuery(studyIds);
  const invalidItemNames = Array.from(studyNamesToStudyablePermission.entries())
    .filter(([, hasPermission]) => !hasPermission)
    .map(([name]) => name);
  const isAutoLinkingDisabled =
    (hasInitialAutoLinkingStudy && !isAutoLinkingStudySelected) || invalidItemNames.length > 0;

  // We want to uncheck the auto-linking checkbox if the auto-linking study is deselected
  useEffect(() => {
    if (!isAutoLinkingStudySelected) {
      setIsAutoLinkingChecked(false);
    }
  }, [isAutoLinkingStudySelected]);

  const handleStudySearchItemsChange = (selectedStudies: Array<StudySearchItem>): void => {
    setStudySearchItems(selectedStudies);
    if (!hasInitialAutoLinkingStudy && !selectedStudies.some((study) => study.id === autoLinkingStudyId)) {
      setAutoLinkingStudyId(selectedStudies[0]?.id);
    }
  };

  return {
    studySearchControlledItemsProps: {
      selectedItems: studySearchItems,
      onChange: handleStudySearchItemsChange,
    },
    invalidItemNames,
    isAutoLinkingChecked,
    isAutoLinkingDisabled,
    onAutoLinkingCheckedChange: (e: InputChangeEvent) => {
      setIsAutoLinkingChecked(e.target.checked);
    },
    onAutoLinkingStudyChange: setAutoLinkingStudyId,
    displayPermissionWarning,
    autoLinkingStudy,
    enableAutoLinkingSelection: undefined,
  };
}

export function getStudyAssociations(
  studyAssociationInputProps: StudyAssociationInputProps
): Array<StudyAssociation> {
  return studyAssociationInputProps.studySearchControlledItemsProps.selectedItems.map((item) => ({
    studyId: item.id,
    isAutoLinking:
      studyAssociationInputProps.isAutoLinkingChecked &&
      item.id === studyAssociationInputProps.autoLinkingStudy?.id,
  }));
}

const styles = StyleSheet.create({
  warningBanner: {
    marginTop: 5,
  },
});
